// frontend/src/hooks/useLanguageSync.ts — sync i18next language from /me.
//
// Mounts inside QueryClientProvider (since it consumes useMe). Pre-auth the
// hook is a no-op because `me` is undefined; post-auth it fires
// `i18n.changeLanguage(me.user.language)` once and stays out of the
// way. Re-fires only when the language actually changes (select projection
// returns the language string; React Query's referential stability keeps the
// effect dep stable across unrelated /me refetches).
//
// One-concern hook — exists separately from ThemeProvider because:
//   - i18n.changeLanguage is a global side effect with its own React tree
//     re-render path (via react-i18next), not a CSS-vars-on-html concern.
//   - Single-responsibility makes the hook trivially testable in isolation.
//   - Future me-derived global side effects (analytics user-id, feature
//     flags, etc.) get a clean sibling location alongside this hook.

import { useEffect } from 'react';
import type { MeResponse } from '@ems-app/schemas/auth/responses';
import i18n from '../i18n/index.js';
import { setFormatLocale } from '../lib/format.js';
import { useMe } from './useMe.js';

const selectLanguage = (me: MeResponse) => me.user.language;
const selectNumerals = (me: MeResponse) => me.user.numerals;

export function useLanguageSync(): void {
  const { data: lang } = useMe(selectLanguage);
  const { data: numerals } = useMe(selectNumerals);

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      void i18n.changeLanguage(lang);
    }
  }, [lang]);

  // Keep the number/date formatter (lib/format.ts) on the server-authoritative
  // language + numerals preference (ADR-030). Server wins; pre-auth this is a
  // no-op (both undefined) and the formatter stays at its en/auto default.
  useEffect(() => {
    if (lang && numerals) setFormatLocale(lang, numerals);
  }, [lang, numerals]);
}
